import React, { useState, useEffect } from 'react';
import * as api from '../api';

// Types
interface SubredditResult {
  name: string;
  members: number;
  min_karma: number;
  self_promo: boolean;
}

interface PostHistory {
  id: number;
  title: string;
  platforms: string;
  status: string;
  posted_at: string | null;
  results: string | null;
}

interface UserData {
  username: string;
  plan: string;
  posts_today: number;
}

const CATEGORIES = [
  { id: 'general', label: 'General / All', tags: ['general'] },
  { id: 'petite', label: 'Petite / Slim', tags: ['petite'] },
  { id: 'curvy', label: 'Curvy / Thick', tags: ['curvy'] },
  { id: 'fitness', label: 'Fitness / Athletic', tags: ['fitness'] },
  { id: 'redhead', label: 'Redhead', tags: ['redhead'] },
  { id: 'brunette', label: 'Brunette', tags: ['brunette'] },
  { id: 'blonde', label: 'Blonde', tags: ['blonde'] },
  { id: 'asian', label: 'Asian', tags: ['asian'] },
  { id: 'latina', label: 'Latina', tags: ['latina'] },
  { id: 'ebony', label: 'Ebony', tags: ['ebony'] },
  { id: 'tattoo', label: 'Tattoo / Alt / Goth', tags: ['tattoo'] },
  { id: 'mature', label: 'MILF / Mature', tags: ['mature'] },
  { id: 'cosplay', label: 'Cosplay / Anime', tags: ['cosplay'] },
  { id: 'feet', label: 'Feet', tags: ['feet'] },
  { id: 'couple', label: 'Couple', tags: ['couple'] },
  { id: 'male', label: 'Male / Gay', tags: ['male'] },
];

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'post' | 'finder' | 'accounts' | 'history' | 'analytics'>('post');
  const [user, setUser] = useState<UserData>({ username: 'Creator', plan: 'free', posts_today: 0 });
  const [loading, setLoading] = useState(true);

  // Load user on mount
  useEffect(() => {
    if (!api.isLoggedIn()) {
      window.location.href = '/login';
      return;
    }
    api.getMe().then(data => {
      setUser({ username: data.username, plan: data.plan, posts_today: data.posts_today });
      setLoading(false);
    }).catch(() => {
      api.logout();
    });
  }, []);

  // Post state
  const [postTitle, setPostTitle] = useState('');
  const [postBody, setPostBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['reddit']);
  const [selectedSubs, setSelectedSubs] = useState<string[]>([]);
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState<string | null>(null);

  // Subreddit Finder state
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [foundSubs, setFoundSubs] = useState<SubredditResult[]>([]);
  const [searching, setSearching] = useState(false);

  // Connected accounts
  const [redditConnected, setRedditConnected] = useState(false);
  const [twitterConnected, setTwitterConnected] = useState(false);
  const [blueskyConnected, setBlueskyConnected] = useState(false);
  const [blueskyHandle, setBlueskyHandle] = useState('');
  const [blueskyPassword, setBlueskyPassword] = useState('');

  // History
  const [history, setHistory] = useState<PostHistory[]>([]);

  const dailyLimit = user.plan === 'free' ? 5 : 999;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4 animate-pulse">🚀</div>
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Toggle platform
  const togglePlatform = (p: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  // Toggle subreddit
  const toggleSub = (name: string) => {
    setSelectedSubs(prev =>
      prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]
    );
  };

  // Select all found subs
  const selectAllSubs = () => {
    setSelectedSubs(foundSubs.map(s => s.name));
  };

  // Toggle category
  const toggleCategory = (id: string) => {
    setSelectedCategories(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Find subreddits
  const findSubreddits = async () => {
    setSearching(true);
    const tags = selectedCategories.flatMap(id => {
      const cat = CATEGORIES.find(c => c.id === id);
      return cat ? cat.tags : [];
    });
    try {
      const data = await api.findSubreddits(tags, true);
      setFoundSubs(data);
    } catch {
      setFoundSubs([]);
    }
    setSearching(false);
  };

  // Create post
  const handleCreatePost = async () => {
    if (!postTitle.trim()) return;
    setPosting(true);
    setPostResult(null);
    try {
      await api.createPost(
        postTitle,
        postBody || null,
        imageUrl || null,
        selectedPlatforms,
        selectedSubs.length > 0 ? selectedSubs : null,
      );
      setPostResult('Posted successfully to ' + selectedPlatforms.join(', ') + '!');
      setPostTitle('');
      setPostBody('');
      setUser(prev => ({ ...prev, posts_today: prev.posts_today + 1 }));
    } catch (e: any) {
      setPostResult('Error: ' + e.message);
    }
    setPosting(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Top bar */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-3 flex items-center justify-between">
        <div className="text-xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
          FanPush
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-400">
            {user.posts_today}/{dailyLimit} posts today
          </span>
          <span className={`text-xs px-2 py-1 rounded-full font-bold uppercase ${
            user.plan === 'pro' ? 'bg-purple-600 text-white' :
            user.plan === 'agency' ? 'bg-orange-600 text-white' :
            'bg-gray-700 text-gray-300'
          }`}>
            {user.plan}
          </span>
          <span className="text-sm text-gray-300">{user.username}</span>
          <button onClick={api.logout} className="text-sm text-gray-500 hover:text-red-400 transition">Logout</button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-gray-900/50 border-r border-gray-800 min-h-[calc(100vh-52px)] p-4">
          <nav className="space-y-1">
            {[
              { id: 'post' as const, icon: '🚀', label: 'New Post' },
              { id: 'finder' as const, icon: '🎯', label: 'Subreddit Finder' },
              { id: 'accounts' as const, icon: '🔗', label: 'Connected Accounts' },
              { id: 'history' as const, icon: '📋', label: 'Post History' },
              { id: 'analytics' as const, icon: '📊', label: 'Analytics' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition ${
                  activeTab === tab.id
                    ? 'bg-purple-600/20 text-purple-400 border border-purple-600/30'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                <span className="text-xl">{tab.icon}</span>
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>

          {user.plan === 'free' && (
            <div className="mt-8 bg-gradient-to-b from-purple-900/30 to-gray-900/30 border border-purple-700/30 rounded-xl p-4">
              <p className="text-sm text-purple-300 font-bold mb-1">Upgrade to Pro</p>
              <p className="text-xs text-gray-400 mb-3">Unlimited posts, all platforms, scheduling</p>
              <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 py-2 rounded-lg text-sm font-bold hover:from-purple-500 hover:to-pink-500 transition">
                $14.99/mo
              </button>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 p-8 max-w-4xl">

          {/* ── NEW POST ── */}
          {activeTab === 'post' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Create New Post</h2>

              {/* Platforms */}
              <div className="mb-6">
                <label className="text-sm text-gray-400 mb-2 block">Post to</label>
                <div className="flex gap-3">
                  {[
                    { id: 'reddit', icon: '🔴', label: 'Reddit', color: 'border-orange-500 bg-orange-500/10' },
                    { id: 'twitter', icon: '🐦', label: 'Twitter/X', color: 'border-blue-500 bg-blue-500/10' },
                    { id: 'bluesky', icon: '🦋', label: 'Bluesky', color: 'border-sky-500 bg-sky-500/10' },
                  ].map(p => (
                    <button
                      key={p.id}
                      onClick={() => togglePlatform(p.id)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition ${
                        selectedPlatforms.includes(p.id)
                          ? p.color + ' text-white'
                          : 'border-gray-700 text-gray-500 hover:border-gray-500'
                      }`}
                    >
                      <span>{p.icon}</span>
                      <span className="font-medium">{p.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div className="mb-4">
                <label className="text-sm text-gray-400 mb-2 block">Title / Tweet text</label>
                <input
                  type="text"
                  value={postTitle}
                  onChange={e => setPostTitle(e.target.value)}
                  placeholder="Your post title..."
                  maxLength={300}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none"
                />
                <div className="text-xs text-gray-600 mt-1 text-right">{postTitle.length}/300</div>
              </div>

              {/* Body */}
              <div className="mb-4">
                <label className="text-sm text-gray-400 mb-2 block">Body (optional)</label>
                <textarea
                  value={postBody}
                  onChange={e => setPostBody(e.target.value)}
                  placeholder="Additional text, link to your page..."
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none resize-none"
                />
              </div>

              {/* Image URL */}
              <div className="mb-4">
                <label className="text-sm text-gray-400 mb-2 block">Image URL (optional)</label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={e => setImageUrl(e.target.value)}
                  placeholder="https://i.imgur.com/your-image.jpg"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none"
                />
              </div>

              {/* Subreddits */}
              {selectedPlatforms.includes('reddit') && (
                <div className="mb-6">
                  <label className="text-sm text-gray-400 mb-2 block">
                    Subreddits ({selectedSubs.length} selected)
                    <button onClick={() => setActiveTab('finder')} className="text-purple-400 hover:text-purple-300 ml-2">
                      Use Subreddit Finder →
                    </button>
                  </label>
                  {selectedSubs.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedSubs.map(s => (
                        <span
                          key={s}
                          onClick={() => toggleSub(s)}
                          className="bg-gray-800 border border-gray-700 px-3 py-1 rounded-full text-sm cursor-pointer hover:border-red-500 hover:text-red-400 transition"
                        >
                          r/{s} ✕
                        </span>
                      ))}
                    </div>
                  )}
                  {selectedSubs.length === 0 && (
                    <p className="text-gray-600 text-sm">No subreddits selected. Use the Subreddit Finder to find the best ones for your niche.</p>
                  )}
                </div>
              )}

              {/* Anti-ban info */}
              <div className="mb-6 bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🛡️</span>
                  <span className="font-bold text-sm">Anti-Ban Protection Active</span>
                </div>
                <p className="text-xs text-gray-500">
                  Titles are automatically varied for each subreddit. Posts are spaced to respect rate limits. NSFW tag is applied automatically.
                </p>
              </div>

              {/* Post button */}
              <button
                onClick={handleCreatePost}
                disabled={posting || !postTitle.trim()}
                className={`w-full py-4 rounded-xl font-bold text-lg transition ${
                  posting || !postTitle.trim()
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-lg hover:scale-[1.01] transform'
                }`}
              >
                {posting ? 'Posting...' : `Post to ${selectedPlatforms.length} platform${selectedPlatforms.length > 1 ? 's' : ''}`}
              </button>

              {postResult && (
                <div className={`mt-4 p-4 rounded-xl border ${
                  postResult.startsWith('Error') ? 'bg-red-900/20 border-red-700 text-red-400' : 'bg-green-900/20 border-green-700 text-green-400'
                }`}>
                  {postResult}
                </div>
              )}
            </div>
          )}

          {/* ── SUBREDDIT FINDER ── */}
          {activeTab === 'finder' && (
            <div>
              <h2 className="text-2xl font-bold mb-2">Smart Subreddit Finder</h2>
              <p className="text-gray-400 mb-6">Select your niche tags and we'll find the best subreddits for your content.</p>

              {/* Category grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    className={`px-4 py-3 rounded-xl border text-sm font-medium transition ${
                      selectedCategories.includes(cat.id)
                        ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                        : 'border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <button
                onClick={findSubreddits}
                disabled={selectedCategories.length === 0 || searching}
                className={`px-8 py-3 rounded-xl font-bold transition mb-8 ${
                  selectedCategories.length === 0
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500'
                }`}
              >
                {searching ? 'Searching...' : `Find Subreddits (${selectedCategories.length} tags)`}
              </button>

              {/* Results */}
              {foundSubs.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg">{foundSubs.length} subreddits found</h3>
                    <div className="flex gap-2">
                      <button onClick={selectAllSubs} className="text-sm text-purple-400 hover:text-purple-300">Select all</button>
                      <button onClick={() => setSelectedSubs([])} className="text-sm text-gray-500 hover:text-gray-400">Clear</button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {foundSubs.map(sub => (
                      <div
                        key={sub.name}
                        onClick={() => toggleSub(sub.name)}
                        className={`flex items-center justify-between px-4 py-3 rounded-xl border cursor-pointer transition ${
                          selectedSubs.includes(sub.name)
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-gray-800 hover:border-gray-600 bg-gray-900/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                            selectedSubs.includes(sub.name) ? 'bg-purple-600 border-purple-600' : 'border-gray-600'
                          }`}>
                            {selectedSubs.includes(sub.name) && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="font-medium">r/{sub.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>{(sub.members / 1000).toFixed(0)}k members</span>
                          {sub.min_karma > 0 && <span className="text-yellow-500">{sub.min_karma} karma min</span>}
                          {sub.self_promo && <span className="text-green-500">Promo OK</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setActiveTab('post')}
                    className="mt-6 bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-3 rounded-xl font-bold hover:from-purple-500 hover:to-pink-500 transition"
                  >
                    Use {selectedSubs.length} subreddits → Create Post
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── CONNECTED ACCOUNTS ── */}
          {activeTab === 'accounts' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Connected Accounts</h2>
              <div className="space-y-4">
                {/* Reddit */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🔴</span>
                      <div>
                        <h3 className="font-bold">Reddit</h3>
                        <p className="text-sm text-gray-500">Post to any subreddit</p>
                      </div>
                    </div>
                    {redditConnected ? (
                      <span className="text-green-400 font-medium">Connected</span>
                    ) : (
                      <button
                        onClick={() => setRedditConnected(true)}
                        className="bg-orange-600 hover:bg-orange-500 px-4 py-2 rounded-lg font-medium transition"
                      >
                        Connect Reddit
                      </button>
                    )}
                  </div>
                </div>

                {/* Twitter */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🐦</span>
                      <div>
                        <h3 className="font-bold">Twitter / X</h3>
                        <p className="text-sm text-gray-500">Auto-tweet your content</p>
                      </div>
                    </div>
                    {twitterConnected ? (
                      <span className="text-green-400 font-medium">Connected</span>
                    ) : (
                      <button
                        onClick={() => setTwitterConnected(true)}
                        className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg font-medium transition"
                      >
                        Connect Twitter
                      </button>
                    )}
                  </div>
                </div>

                {/* Bluesky */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🦋</span>
                      <div>
                        <h3 className="font-bold">Bluesky</h3>
                        <p className="text-sm text-gray-500">Post to Bluesky</p>
                      </div>
                    </div>
                    {blueskyConnected && <span className="text-green-400 font-medium">Connected</span>}
                  </div>
                  {!blueskyConnected && (
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={blueskyHandle}
                        onChange={e => setBlueskyHandle(e.target.value)}
                        placeholder="handle.bsky.social"
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none"
                      />
                      <input
                        type="password"
                        value={blueskyPassword}
                        onChange={e => setBlueskyPassword(e.target.value)}
                        placeholder="App password"
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none"
                      />
                      <button
                        onClick={() => setBlueskyConnected(true)}
                        className="bg-sky-600 hover:bg-sky-500 px-4 py-2 rounded-lg font-medium transition text-sm"
                      >
                        Connect
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── POST HISTORY ── */}
          {activeTab === 'history' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Post History</h2>
              {history.length === 0 ? (
                <div className="text-center py-16">
                  <span className="text-6xl mb-4 block">📋</span>
                  <p className="text-gray-500 text-lg">No posts yet</p>
                  <p className="text-gray-600">Create your first post to see it here</p>
                  <button
                    onClick={() => setActiveTab('post')}
                    className="mt-4 bg-purple-600 hover:bg-purple-500 px-6 py-2 rounded-lg font-medium transition"
                  >
                    Create Post
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map(post => (
                    <div key={post.id} className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{post.title}</p>
                        <p className="text-sm text-gray-500">{post.posted_at}</p>
                      </div>
                      <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                        post.status === 'posted' ? 'bg-green-900/30 text-green-400' :
                        post.status === 'failed' ? 'bg-red-900/30 text-red-400' :
                        'bg-yellow-900/30 text-yellow-400'
                      }`}>
                        {post.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ANALYTICS ── */}
          {activeTab === 'analytics' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Analytics</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Total Posts', value: '0', icon: '📤', color: 'text-purple-400' },
                  { label: 'Today', value: `${user.posts_today}/${dailyLimit}`, icon: '📅', color: 'text-pink-400' },
                  { label: 'Platforms', value: '3', icon: '🌐', color: 'text-orange-400' },
                  { label: 'Subreddits', value: `${selectedSubs.length}`, icon: '🎯', color: 'text-green-400' },
                ].map((stat, i) => (
                  <div key={i} className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 text-center">
                    <span className="text-2xl block mb-2">{stat.icon}</span>
                    <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                    <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {user.plan === 'free' && (
                <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-700/30 rounded-xl p-6 text-center">
                  <h3 className="font-bold text-lg mb-2">Unlock Advanced Analytics</h3>
                  <p className="text-gray-400 mb-4">See which subreddits drive the most subscribers, best posting times, and engagement metrics.</p>
                  <button className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-3 rounded-xl font-bold hover:from-purple-500 hover:to-pink-500 transition">
                    Upgrade to Pro — $14.99/mo
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
